/**
 * Google Automated Discounts (Google Otomatik İndirimler) Integration
 * Web Crypto API (ES256) JWT Signature Verification & Dynamic Frontend Pricing
 * 
 * =============================================================================
 * SAHINKAYA GÜNCELLEMESİ
 * Geliştirici: Şahinkaya
 * Son Düzenleme Tarihi: 10 Temmuz 2026
 * =============================================================================
 * 
 * Bu script, Google Merchant Center'dan gelen 'pv2' parametreli JSON Web Token'ları (JWT)
 * tarayıcı tarafında kriptografik olarak doğrular, geçerliyse yerel depolamada saklar
 * ve ürün detay sayfası (en az 30 dk) ile sepette (en az 48 saat) indirimli fiyatları
 * orijinal fiyatın üstü çizili ve indirimli fiyatın kırmızı renkte çıkacağı şekilde gösterir.
 */

(function () {
  'use strict';

  // Google'ın resmi genel anahtarı (PEM formatındaki Public Key DER verisinin Base64 kısmı)
  const GOOGLE_PUBLIC_KEY_BASE64 = "MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAERUlUpxshr67EO66ZTX0Fpog0LEHcnUnlSsIrOfroxTLu2XnigBK/lfYRxzQWq9K6nqsSjjYeea0T12r+y3nvqg==";

  // İsteğe bağlı: Satıcı Kimliğiniz (Merchant ID). Güvenlik için doğrulama yapılmasını istiyorsanız buraya GMC ID'nizi yazabilirsiniz.
  // null veya boş bırakılırsa satıcı kimliği eşleşme kontrolü atlanır.
  const EXPECTED_MERCHANT_ID = null; 

  const STORAGE_KEY = 'google_automated_discounts';

  /**
   * Base64URL kodlu metni standart Base64'e dönüştürür.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function base64UrlDecode(str) {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    return base64;
  }

  /**
   * Base64 metni Uint8Array byte dizisine dönüştürür.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function base64ToBytes(base64) {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * SPKI formatındaki genel anahtarı Web Crypto API ile içe aktarır.
   * SAHINKAYA GÜNCELLEMESİ
   */
  async function importPublicKey(pemBase64) {
    const bytes = base64ToBytes(pemBase64);
    return window.crypto.subtle.importKey(
      "spki",
      bytes,
      {
        name: "ECDSA",
        namedCurve: "P-256"
      },
      true,
      ["verify"]
    );
  }

  /**
   * JWT imzasını doğrular ve kodunu çözer.
   * SAHINKAYA GÜNCELLEMESİ - Üretim ve güvenlik için imza doğrulaması zorunludur.
   */
  async function verifyAndDecodeJwt(jwtString, publicKey) {
    const parts = jwtString.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const headerSeg = parts[0];
    const payloadSeg = parts[1];
    const signatureSeg = parts[2];

    try {
      // 1. Başlık alanlarının doğrulanması
      const headerStr = window.atob(base64UrlDecode(headerSeg));
      const header = JSON.parse(headerStr);
      if (header.alg !== "ES256" || header.typ !== "JWT" || Object.keys(header).length !== 2) {
        return null;
      }

      // 2. İmza Doğrulaması (ECDSA SHA-256)
      const encoder = new TextEncoder();
      const dataBytes = encoder.encode(headerSeg + '.' + payloadSeg);
      const sigBytes = base64ToBytes(base64UrlDecode(signatureSeg));

      const isValid = await window.crypto.subtle.verify(
        {
          name: "ECDSA",
          hash: { name: "SHA-256" }
        },
        publicKey,
        sigBytes,
        dataBytes
      );

      if (!isValid) {
        return null;
      }

      // 3. Yükün parse edilmesi
      const payloadStr = window.atob(base64UrlDecode(payloadSeg));
      return JSON.parse(payloadStr);

    } catch (e) {
      return null;
    }
  }

  /**
   * Stored indirimleri döndürür veya temiz bir nesne oluşturur.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getStoredDiscounts() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * İndirimi yerel depolamaya kaydeder (aynı variant için olanı günceller veya ekler).
   * SAHINKAYA GÜNCELLEMESİ
   */
  function saveDiscount(payload) {
    const stored = getStoredDiscounts();
    const key = String(payload.o).toLowerCase(); // Teklif numarası (offer_id / SKU / ID)

    stored[key] = {
      o: payload.o,               // Teklif no / SKU / Varyant ID
      m: payload.m,               // Satıcı Kimliği
      c: payload.c,               // Para birimi
      p: parseFloat(payload.p),   // İndirimli yeni fiyat
      pp: payload.pp ? parseFloat(payload.pp) : null, // Önceki fiyat
      timestamp: Date.now()       // Kaydedilme zamanı (30 dk ve 48 saat takibi için)
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {
      console.error("[Google Discounts] localStorage kaydetme hatası:", e);
    }
  }

  /**
   * Süresi dolmuş indirimleri temizler.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function cleanExpiredDiscounts() {
    const stored = getStoredDiscounts();
    const now = Date.now();
    let changed = false;

    for (const key in stored) {
      const discount = stored[key];
      // 48 saati aşan tüm kayıtları kalıcı olarak temizle
      if (now - discount.timestamp > 48 * 60 * 60 * 1000) {
        delete stored[key];
        changed = true;
      }
    }

    if (changed) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (_) {}
    }
  }

  /**
   * Google'dan gelen pv2 parametresini işler.
   * SAHINKAYA GÜNCELLEMESİ - Üretim sürümü için tüm geçici test atlamaları kaldırıldı.
   */
  async function processUrlToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('pv2');

    if (!token) return;

    if (!window.crypto || !window.crypto.subtle) {
      return;
    }

    try {
      const publicKey = await importPublicKey(GOOGLE_PUBLIC_KEY_BASE64);
      const payload = await verifyAndDecodeJwt(token, publicKey);

      if (!payload) return;

      const nowInSeconds = Math.floor(Date.now() / 1000);

      // exp kontrolü (Unix zamanı olarak jeton geçerliliği)
      if (nowInSeconds > payload.exp) {
        return;
      }

      // Satıcı kimliği kontrolü (m eşleşmeli)
      if (EXPECTED_MERCHANT_ID && String(payload.m) !== String(EXPECTED_MERCHANT_ID)) {
        return;
      }

      // Para birimi kontrolü (Mağazanın aktif para birimiyle eşleşmeli)
      const shopCurrency = window.Shopify?.currency?.active || 'TRY';
      if (payload.c && String(payload.c).toUpperCase() !== String(shopCurrency).toUpperCase()) {
        return;
      }

      // Her şey geçerliyse depolamaya kaydet
      saveDiscount(payload);

      // Temiz bir görünüm için URL'deki pv2 parametresini kaldır
      urlParams.delete('pv2');
      const newQuery = urlParams.toString();
      const newUrl = window.location.pathname + (newQuery ? '?' + newQuery : '') + window.location.hash;
      window.history.replaceState({ path: newUrl }, '', newUrl);

    } catch (e) {
      console.error("[Google Discounts] Jeton işleme hatası:", e);
    }
  }

  /**
   * Shopify Variant ID, SKU veya Product ID ile teklif numarasını (offer_id / "o") eşleştirir.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function matchOfferId(payloadOfferId, currentVariantId, currentVariantSku, currentProductId) {
    const o = String(payloadOfferId).toLowerCase().trim();
    const vid = currentVariantId ? String(currentVariantId).toLowerCase().trim() : '';
    const sku = currentVariantSku ? String(currentVariantSku).toLowerCase().trim() : '';
    if (!o) return false;

    // SAHINKAYA — exact matching only. The Google & YouTube channel emits offer IDs as
    // "shopify_<COUNTRY>_<productId>_<variantId>"; compare the trailing variant id exactly.
    // Substring matching (o.includes(pid)) previously applied one variant's Google price to
    // every other variant of the same product. Product-id matching is intentionally dropped.
    const channelMatch = o.match(/^shopify_[a-z]{2}_\d+_(\d+)$/);
    if (channelMatch) {
      return !!vid && channelMatch[1] === vid;
    }
    return (!!vid && o === vid) || (!!sku && o === sku);
  }

  /**
   * Sayfadaki veya formdaki aktif varyant ID'sini çözer.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getActiveVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) return urlVariant;

    const idInput = document.querySelector('.shopify-section--main-product form[action*="/cart/add"] input[name="id"]');
    if (idInput && idInput.value) return idInput.value;

    const prod = getProductInfo();
    if (prod && prod.variants && prod.variants.length > 0) {
      return String(prod.variants[0].id);
    }
    return null;
  }

  /**
   * Sayfadaki aktif varyantın SKU'sunu çözer.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getActiveVariantSku() {
    const variantId = getActiveVariantId();
    const prod = getProductInfo();
    if (prod && prod.variants && variantId) {
      const active = prod.variants.find(v => String(v.id) === String(variantId));
      if (active && active.sku) return active.sku;
    }
    return null;
  }

  /**
   * Shopify ürün meta verilerini döner.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getProductInfo() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product;
    }
    if (window.meta && window.meta.product) {
      return window.meta.product;
    }
    return null;
  }

  /**
   * Fiyatı para birimiyle biçimlendirir.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function formatCurrency(amount, currencyCode) {
    const loc = window.Shopify?.locale === 'tr' ? 'tr-TR' : 'en-US';
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: currencyCode || 'TRY',
      currencyDisplay: 'symbol'
    }).format(amount);
  }

  /**
   * Elementten orijinal (indirimden önceki) fiyatı parse edip önbelleğe alır.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getOriginalPriceFromDOM(element) {
    if (element.hasAttribute('data-original-price')) {
      return parseFloat(element.getAttribute('data-original-price'));
    }
    const compareEl = element.querySelector('compare-at-price');
    if (compareEl && compareEl.textContent) {
      const price = parsePriceText(compareEl.textContent);
      if (price) {
        element.setAttribute('data-original-price', price);
        return price;
      }
    }
    const saleEl = element.querySelector('sale-price');
    if (saleEl && saleEl.textContent) {
      const price = parsePriceText(saleEl.textContent);
      if (price) {
        element.setAttribute('data-original-price', price);
        return price;
      }
    }
    return null;
  }

  /**
   * Fiyat metninden float sayı elde eder. (Örn: "1.200,00 TL" -> 1200)
   * SAHINKAYA GÜNCELLEMESİ
   */
  function parsePriceText(text) {
    const clean = text.replace(/[^\d.,]/g, '').trim();
    if (!clean) return null;
    // SAHINKAYA — the store money format is "{{amount}}" (e.g. "5,250.00"), but locale
    // formatting can also yield "5.250,00". Whichever separator appears LAST is the decimal
    // separator; the other one is a thousands separator. A lone separator followed by
    // exactly 2 digits is decimal ("1200,00" / "1200.00"); otherwise it is thousands.
    const lastDot = clean.lastIndexOf('.');
    const lastComma = clean.lastIndexOf(',');
    let normalized;
    if (lastDot !== -1 && lastComma !== -1) {
      normalized = lastComma > lastDot
        ? clean.replace(/\./g, '').replace(',', '.')
        : clean.replace(/,/g, '');
    } else if (lastComma !== -1) {
      const parts = clean.split(',');
      normalized = (parts.length === 2 && parts[1].length === 2) ? clean.replace(',', '.') : clean.replace(/,/g, '');
    } else if (lastDot !== -1) {
      const parts = clean.split('.');
      normalized = (parts.length === 2 && parts[1].length === 2) ? clean : clean.replace(/\./g, '');
    } else {
      normalized = clean;
    }
    const value = parseFloat(normalized);
    return Number.isFinite(value) ? value : null;
  }

  /**
   * Sepet satır öğesinden variant_id ve SKU çözer.
   * SAHINKAYA GÜNCELLEMESİ
   */
  function getLineItemIdentifiers(lineItemEl) {
    let variantId = null;
    let sku = null;

    const link = lineItemEl.querySelector('a[href*="variant="]');
    if (link) {
      const url = new URL(link.href, window.location.origin);
      variantId = url.searchParams.get('variant');
    }

    // Alternatif variant_id tespiti (remove link vb.)
    if (!variantId) {
      const removeLink = lineItemEl.querySelector('a[href*="/cart/change"], a[href*="id="]');
      if (removeLink) {
        const match = removeLink.href.match(/[?&]id=([^&:]+)/);
        if (match && match[1]) variantId = match[1];
      }
    }

    return { variantId, sku };
  }

  /**
   * İndirimli fiyatları sayfada ve sepette gösterir (ANA DÖNGÜ).
   * SAHINKAYA GÜNCELLEMESİ
   */
  function applyActiveDiscounts() {
    const stored = getStoredDiscounts();
    if (Object.keys(stored).length === 0) return;

    const now = Date.now();

    // 1. ÜRÜN DETAY SAYFASI (Landing Page) UYGULAMASI (Min 30 dakika geçerli)
    const isProductPage = window.location.pathname.includes('/products/');
    if (isProductPage) {
      const currentVariantId = getActiveVariantId();
      const currentProductId = getProductInfo() ? String(getProductInfo().id) : null;
      const currentVariantSku = getActiveVariantSku();

      let activeDiscount = null;
      for (const key in stored) {
        const d = stored[key];
        // 30 dakikayı aşmamış olması koşulu
        if (now - d.timestamp < 30 * 60 * 1000) {
          if (matchOfferId(d.o, currentVariantId, currentVariantSku, currentProductId)) {
            activeDiscount = d;
            break;
          }
        }
      }

      if (activeDiscount) {
        const priceLists = document.querySelectorAll('.shopify-section--main-product price-list, .shopify-section--main-product .price-list');
        priceLists.forEach(priceList => {
          // İndirimli yeni fiyatı göster (Kırmızı Renkte)
          const salePriceEl = priceList.querySelector('sale-price');
          if (salePriceEl) {
            salePriceEl.textContent = formatCurrency(activeDiscount.p, activeDiscount.c);
            salePriceEl.style.setProperty('color', 'red', 'important');
            salePriceEl.style.setProperty('font-weight', 'bold', 'important');
          }

          // Önceki orijinal fiyatın üstünü çiz
          let comparePriceEl = priceList.querySelector('compare-at-price');
          const origPrice = activeDiscount.pp || getOriginalPriceFromDOM(priceList) || activeDiscount.p;
          if (origPrice && origPrice > activeDiscount.p) {
            if (!comparePriceEl) {
              comparePriceEl = document.createElement('compare-at-price');
              comparePriceEl.className = 'price price--compare-at text-subdued line-through';
              priceList.appendChild(comparePriceEl);
            }
            comparePriceEl.textContent = formatCurrency(origPrice, activeDiscount.c);
            comparePriceEl.removeAttribute('hidden');
            comparePriceEl.style.setProperty('display', 'inline', 'important');
            comparePriceEl.style.setProperty('text-decoration', 'line-through', 'important');
          }
        });
      }
    }

    // 2. ALIŞVERİŞ SEPETİ & DRAWER UYGULAMASI (Min 48 saat geçerli)
    const lineItems = document.querySelectorAll('line-item, .line-item');
    lineItems.forEach(lineItem => {
      const ids = getLineItemIdentifiers(lineItem);
      
      let activeDiscount = null;
      for (const key in stored) {
        const d = stored[key];
        if (now - d.timestamp < 48 * 60 * 60 * 1000) { // 48 saat geçerlilik
          if (matchOfferId(d.o, ids.variantId, ids.sku, null)) {
            activeDiscount = d;
            break;
          }
        }
      }

      if (activeDiscount) {
        const priceLists = lineItem.querySelectorAll('price-list, .price-list');
        priceLists.forEach(priceList => {
          // İndirimli fiyat (Kırmızı)
          const salePriceEl = priceList.querySelector('sale-price');
          if (salePriceEl) {
            salePriceEl.textContent = formatCurrency(activeDiscount.p, activeDiscount.c);
            salePriceEl.style.setProperty('color', 'red', 'important');
          }

          // Önceki fiyat (Üstü çizili)
          let comparePriceEl = priceList.querySelector('compare-at-price');
          const origPrice = activeDiscount.pp || getOriginalPriceFromDOM(priceList) || activeDiscount.p;
          if (origPrice && origPrice > activeDiscount.p) {
            if (!comparePriceEl) {
              comparePriceEl = document.createElement('compare-at-price');
              comparePriceEl.className = 'price price--compare-at text-subdued line-through';
              priceList.appendChild(comparePriceEl);
            }
            comparePriceEl.textContent = formatCurrency(origPrice, activeDiscount.c);
            comparePriceEl.removeAttribute('hidden');
            comparePriceEl.style.setProperty('display', 'inline', 'important');
            comparePriceEl.style.setProperty('text-decoration', 'line-through', 'important');
          }
        });
      }
    });
  }

  // Başlatma ve Döngüler
  async function init() {
    cleanExpiredDiscounts();
    await processUrlToken();
    applyActiveDiscounts();

    // Dinamik sepet ve varyant değişimlerine anlık ayak uydurmak için
    setInterval(applyActiveDiscounts, 400);

    // Varyant değiştiğinde de anında fiyatı güncelle
    document.addEventListener('variant:change', () => {
      setTimeout(applyActiveDiscounts, 50);
    });
  }

  init();

})();
